/**
 * "My Saved Views" — the single control for the user's views (#1144, R1).
 *
 * Two variants, one row implementation:
 * - `list` (Legislation page card): an always-visible scrolling list, starred
 *   (primary) views sorted to the top. Creation is the card's New View button,
 *   so the list itself has no footer action.
 * - `dropdown` (Bill Tracking toolbar): the compact trigger + panel, where an
 *   inline list would not fit. Includes a "New view" footer item.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, Star } from 'lucide-react';
import { EditIcon, DeleteIcon } from '../icons';
import type { SavedView } from '../../types/savedViews';

export interface SavedViewsControlProps {
  views: SavedView[];
  /** The applied view, if any. */
  activeViewId: string | null;
  /** True when the table no longer matches the applied view (R16). */
  isModified?: boolean;
  onApply: (view: SavedView) => void;
  /*
    Authoring handlers are OPTIONAL (PRD §15): views are created and edited by
    org_admins from the Legislation page only. A surface that omits these — the
    Bill Tracking tab, or the Legislation page for a non-admin — renders no New
    view / Edit / Delete affordances. Starring stays: the primary is personal.
  */
  onNew?: () => void;
  onEdit?: (view: SavedView) => void;
  /** Omit (with onMakeDefaultPrimary) for an apply-only picker: no star buttons. */
  onMakePrimary?: (view: SavedView) => void;
  onDelete?: (view: SavedView) => void;
  /** Apply the built-in Default View — the original table, all columns, default filters. */
  onApplyDefault: () => void;
  /** Star on the Default row: clears any saved-view primary so Default loads by default. */
  onMakeDefaultPrimary?: () => void;
  className?: string;
  /** `list` renders inline and scrolls; `dropdown` is the compact trigger+panel. */
  variant?: 'dropdown' | 'list';
  /** List variant: stretch to the parent's row height (list scrolls inside) instead of the fixed cap. */
  fillHeight?: boolean;
}

/** One view row — shared by both variants so behaviour cannot drift. */
const ViewRow: React.FC<{
  view: SavedView;
  isActive: boolean;
  onApply: () => void;
  onEdit?: () => void;
  onMakePrimary?: () => void;
  onDelete?: () => void;
}> = ({ view, isActive, onApply, onEdit, onMakePrimary, onDelete }) => (
  <div
    className={`group flex items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-primary-50 dark:hover:bg-background-dark-tertiary ${
      isActive ? 'bg-primary-50 dark:bg-background-dark-tertiary' : ''
    }`}
  >
    <button
      type="button"
      onClick={onApply}
      className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-text-secondary dark:text-text-dark-secondary"
    >
      <span className="w-4 flex-none">
        {isActive && (
          <Check className="h-4 w-4 text-primary-700 dark:text-primary-400" strokeWidth={3} />
        )}
      </span>
      <span className="truncate">{view.name}</span>
      {view.isPrimary && (
        <Star
          className="h-3.5 w-3.5 flex-none fill-amber-400 text-amber-400"
          aria-label="Primary view"
        />
      )}
    </button>
    {onEdit && (
      <button
        type="button"
        aria-label={`Edit ${view.name}`}
        title="Edit"
        onClick={onEdit}
        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary"
      >
        <EditIcon className="w-4 h-4" />
      </button>
    )}
    {onMakePrimary && !view.isPrimary && (
      <button
        type="button"
        aria-label={`Make ${view.name} primary`}
        title="Make primary"
        onClick={onMakePrimary}
        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-amber-500"
      >
        <Star className="w-4 h-4" />
      </button>
    )}
    {onDelete && (
      <button
        type="button"
        aria-label={`Delete ${view.name}`}
        title="Delete"
        onClick={onDelete}
        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-error-600"
      >
        <DeleteIcon className="w-4 h-4" />
      </button>
    )}
  </div>
);

/**
 * The built-in Default View — the original table with every column and the
 * default filters. Not stored, not editable, not deletable; always first.
 * Its star is filled when NO saved view is primary, because "Default is
 * primary" simply means no primary row exists.
 */
const DefaultViewRow: React.FC<{
  isActive: boolean;
  isEffectivePrimary: boolean;
  onApply: () => void;
  onMakePrimary?: () => void;
}> = ({ isActive, isEffectivePrimary, onApply, onMakePrimary }) => (
  <div
    className={`group flex items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-primary-50 dark:hover:bg-background-dark-tertiary ${
      isActive ? 'bg-primary-50 dark:bg-background-dark-tertiary' : ''
    }`}
  >
    <button
      type="button"
      onClick={onApply}
      className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-text-secondary dark:text-text-dark-secondary"
    >
      <span className="w-4 flex-none">
        {isActive && (
          <Check className="h-4 w-4 text-primary-700 dark:text-primary-400" strokeWidth={3} />
        )}
      </span>
      <span className="truncate font-medium">Default View</span>
      {isEffectivePrimary && (
        <Star
          className="h-3.5 w-3.5 flex-none fill-amber-400 text-amber-400"
          aria-label="Loads by default"
        />
      )}
    </button>
    {onMakePrimary && !isEffectivePrimary && (
      <button
        type="button"
        aria-label="Make Default View primary"
        title="Make primary"
        onClick={onMakePrimary}
        className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-amber-500"
      >
        <Star className="w-4 h-4" />
      </button>
    )}
  </div>
);

const SavedViewsControl: React.FC<SavedViewsControlProps> = ({
  views,
  activeViewId,
  isModified = false,
  onApply,
  onNew,
  onEdit,
  onMakePrimary,
  onDelete,
  onApplyDefault,
  onMakeDefaultPrimary,
  className = '',
  variant = 'dropdown',
  fillHeight = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase — see MultiSelectDropdown: the shared Modal's panel stops
    // mousedown propagation, which starves bubble-phase document listeners.
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open]);

  // Starred (primary) at the top; the API already orders this way, sorted
  // again here so a local upsert can never present a different order.
  const sortedViews = useMemo(
    () => [...views].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
    [views]
  );

  const hasPrimary = views.some((v) => v.isPrimary);

  const defaultRow = (closeAfterApply: boolean) => (
    <DefaultViewRow
      isActive={activeViewId === null}
      isEffectivePrimary={!hasPrimary}
      onApply={() => {
        onApplyDefault();
        if (closeAfterApply) setOpen(false);
      }}
      onMakePrimary={onMakeDefaultPrimary}
    />
  );

  const rows = (closeAfterApply: boolean) =>
    sortedViews.map((view) => (
      <ViewRow
        key={view.id}
        view={view}
        isActive={view.id === activeViewId}
        onApply={() => {
          onApply(view);
          if (closeAfterApply) setOpen(false);
        }}
        onEdit={
          onEdit
            ? () => {
                onEdit(view);
                if (closeAfterApply) setOpen(false);
              }
            : undefined
        }
        onMakePrimary={onMakePrimary ? () => onMakePrimary(view) : undefined}
        onDelete={onDelete ? () => onDelete(view) : undefined}
      />
    ));

  if (variant === 'list') {
    return (
      <div className={`${className} ${fillHeight ? 'flex flex-col min-h-0 lg:flex-1' : ''}`}>
        {isModified && activeViewId && (
          <p className="mb-1.5 text-xs italic text-text-tertiary dark:text-text-dark-tertiary">
            Filters changed since this view was applied — edit the view to keep them.
          </p>
        )}
        <div
          className={`overflow-y-auto rounded-lg border border-border-light dark:border-border-dark p-1.5 ${
            /* fillHeight: match the Filters card's row height on lg (the grid
               stretches this card; the list absorbs the slack and scrolls).
               Below lg the cards stack, so the fixed cap returns. */
            fillHeight ? 'max-h-72 lg:max-h-none lg:flex-1 lg:min-h-0' : 'max-h-72'
          }`}
          role="listbox"
          aria-label="My Saved Views"
        >
          {defaultRow(false)}
          {rows(false)}
          {sortedViews.length === 0 && (
            <p className="px-1 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
              {onNew
                ? 'No saved views yet. Create one with New View.'
                : 'No saved views yet. Your organization admins create them.'}
            </p>
          )}
        </div>
      </div>
    );
  }

  const active = views.find((v) => v.id === activeViewId) ?? null;
  const summary = active ? `${active.name}${isModified ? ' (modified)' : ''}` : 'Default View';

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="My Saved Views"
        /* Inline border for the same reason as MultiSelectDropdown: the unlayered
           Ionic `button { border: 0 }` reset beats class-based borders. */
        style={{ borderWidth: '1px', borderStyle: 'solid' }}
        className="input flex items-center justify-between gap-2 text-left text-sm border border-border-light-secondary dark:border-border-dark min-w-[180px]"
      >
        <span className={`truncate ${isModified ? 'italic' : ''}`}>{summary}</span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-text-tertiary dark:text-text-dark-tertiary" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 w-80 max-h-96 overflow-y-auto rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary p-1.5 shadow-lg"
        >
          {defaultRow(true)}
          {rows(true)}
          {sortedViews.length === 0 && (
            <p className="px-2 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
              No saved views yet.
            </p>
          )}
          {onNew && (
            <div className="mt-1 border-t border-border-light dark:border-border-dark pt-1">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onNew();
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-primary-700 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-background-dark-tertiary"
              >
                <Plus className="w-4 h-4" />
                New view
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedViewsControl;
